import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
const VIEWS = new Set(['accounts-admin', 'admin', 'equipment-admin', 'archive', 'requests', 'requests-admin', 'home', 'dashboard', 'playerSearch', 'playerDetail', 'matches', 'addMatch', 'playerRegistration', 'tournamentNews', 'tournamentRegister', 'schedule', 'scheduleRegister', 'announcements', 'announcements-admin', 'admin-login', 'eloExplanation', 'more', 'about']);
const REQUEST_STATUSES = new Set(['all', 'pending', 'approved', 'checked_out', 'returned', 'rejected', 'cancelled']);
const TABS = new Set(['doubles', 'friendly', 'badge']);
export function parseClubLocation(hash = '') {
  const [path, query = ''] = hash.replace(/^#/, '').split('?');
  const [rawView, rawId] = path.split('/');
  let playerId = null;
  try { playerId = rawId ? decodeURIComponent(rawId) : null; } catch { /* Invalid shared link. */ }
  const params = new URLSearchParams(query);
  const tab = params.get('tab');
  const requestStatus = REQUEST_STATUSES.has(params.get('status')) ? params.get('status') : 'pending';
  return { view: VIEWS.has(rawView) ? rawView : 'home', ...(rawView === 'requests-admin' ? { requestStatus } : {}), playerId, dashboardTab: TABS.has(tab) ? tab : 'doubles' };
}
export function clubHash(route) {
  return `#${route.view}${route.view === 'playerDetail' && route.playerId ? `/${encodeURIComponent(route.playerId)}` : ''}${route.view === 'dashboard' ? `?tab=${route.dashboardTab}` : route.view === 'requests-admin' ? `?status=${route.requestStatus || 'pending'}` : ''}`;
}
export default function useClubNavigation() {
  const [route, setRoute] = useState(() => parseClubLocation(window.location.hash));
  const routeRef = useRef(route);
  const restoreY = useRef(null);
  const listState = useRef({});
  useEffect(() => {
    const sync = () => {
      const next = parseClubLocation(window.location.hash);
      routeRef.current = next;
      restoreY.current = window.history.state?.scrollY || 0;
      setRoute(next);
    };
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    window.history.replaceState({ ...window.history.state, ...routeRef.current }, '', clubHash(routeRef.current));
    window.addEventListener('popstate', sync);
    window.addEventListener('hashchange', sync);
    return () => { window.removeEventListener('popstate', sync); window.removeEventListener('hashchange', sync); window.history.scrollRestoration = previous; };
  }, []);
  useLayoutEffect(() => {
    if (restoreY.current !== null) { window.scrollTo(0, restoreY.current); restoreY.current = null; }
  }, [route]);
  const navigate = useCallback((view, playerId, options = {}) => {
    const current = routeRef.current;
    const next = { ...current, view, ...(view === 'requests-admin' ? { requestStatus: REQUEST_STATUSES.has(options.requestStatus) ? options.requestStatus : 'pending' } : {}), playerId: view === 'playerDetail' ? (playerId || current.playerId) : null };
    if (!VIEWS.has(view) || clubHash(next) === clubHash(current)) return;
    window.history.replaceState({ ...window.history.state, ...current, scrollY: window.scrollY }, '');
    window.history.pushState({ ...next, scrollY: 0 }, '', clubHash(next));
    routeRef.current = next; restoreY.current = 0; setRoute(next);
  }, []);
  const setDashboardTab = useCallback((dashboardTab) => {
    if (!TABS.has(dashboardTab)) return;
    const next = { ...routeRef.current, dashboardTab };
    routeRef.current = next;
    window.history.replaceState({ ...window.history.state, ...next }, '', clubHash(next));
    setRoute(next);
  }, []);
  return { route, navigate, setDashboardTab, listState };
}
